import { Injectable, Injector, forwardRef, Inject } from '@angular/core';
import {
  HttpInterceptor, HttpRequest, HttpHandler,
  HttpEvent, HttpErrorResponse, HttpStatusCode
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/services/auth.service';

/**
 * Last-resort logout handler.
 * Triggers logout only when a 401 arrives AFTER a token-refresh retry
 * (i.e. X-Token-Refresh-Retry header is present).
 *
 * Uses forwardRef + Injector to safely break the circular dependency:
 * AuthService → HttpClient → Interceptors → AuthService
 */
@Injectable()
export class ApiUnauthorisedInterceptor implements HttpInterceptor {
  static readonly RETRY_HEADER = 'X-Token-Refresh-Retry';

  private isLoggingOut = false;

  private readonly SKIP_URLS = [
    '/api/CAS/login',
    '/api/CAS/signout',
    '/api/CAS/tokenRefresh',
    '/api/Authorization/RefreshToken',
    '/companies/user/change/password'
  ];

  constructor(
    @Inject(forwardRef(() => Injector)) private injector: Injector
  ) {}

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.SKIP_URLS.some(u => req.url.includes(u))) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldForceLogout(error, req) && !this.isLoggingOut) {
          this.isLoggingOut = true;
          this.authService.logout().finally(() => {
            setTimeout(() => { this.isLoggingOut = false; }, 5000);
          });
        }
        return throwError(() => error);
      })
    );
  }

  private shouldForceLogout(error: HttpErrorResponse, req: HttpRequest<any>): boolean {
    if (!(error instanceof HttpErrorResponse)) return false;
    if (error.status === HttpStatusCode.Unauthorized) {
      // Only force logout if a token refresh was already attempted
      return req.headers.has(ApiUnauthorisedInterceptor.RETRY_HEADER);
    }
    return false;
  }
}
